// TMPContent.jsx
import { useAuth } from '../contexts/AuthContext';
import { useYear } from '../contexts/YearContext';
import { useTMPData } from '../hooks/tmp/useTMPData';
import { useCalculMap } from '../hooks/tmp/useCalculMap';
import { useTrimestreAggregation } from '../hooks/tmp/useTrimestreAggregation';
import { colors } from '../styles/colors';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import FilterBar from './FilterBar';
import ActionButton from './ActionButton';
import AnimatedContent from './AnimatedContent';
import TrimestreCard from './temps/TrimestreCard';
import RecalculConfirmModal from './temps/RecalculConfirmModal';

export default function TMPContent() {
  const { hasPermission, ACTIONS, RESOURCES } = useAuth();
  const canEditTMP = hasPermission(ACTIONS.WRITE, RESOURCES.TMP);
  const { selectedYear: yearFilter, setSelectedYear: setYearFilter, availableYears } = useYear();

  // 1. Données
  const {
    calculTMP, tempsByYear,
    loading, error,
    recalculLoading, recalculResult, setRecalculResult,
    showRecalculConfirm, setShowRecalculConfirm,
    handleRecalcul, confirmRecalcul
  } = useTMPData();

  // 2. Mapping
  const calculMap = useCalculMap(calculTMP);

  // 3. Calculs
  const trimestreData = useTrimestreAggregation(tempsByYear, yearFilter, calculMap);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Noto Sans", sans-serif'
    }}>
      {/* Barre de filtres */}
      <FilterBar
        filters={[
          {
            type: 'select',
            value: yearFilter,
            onChange: setYearFilter,
            options: availableYears.map(y => ({ value: y, label: y.toString() })),
            allowNull: false,
            minWidth: '120px'
          }
        ]}
        actionButtons={
          <ActionButton
            icon={RefreshCw}
            text="Recalculer TMP"
            loadingText="Recalcul..."
            onClick={() => canEditTMP && handleRecalcul()}
            loading={recalculLoading}
            disabled={!canEditTMP}
            variant="primary"
          />
        }
        marginBottom="1rem"
      />

      {/* Message d'erreur */}
      {error && (
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          backgroundColor: colors.background.lightRed,
          padding: '1rem',
          borderRadius: '0.5rem',
          border: `1px solid ${colors.status.error}`,
          marginBottom: '1rem'
        }}>
          <AlertTriangle color={colors.hover.errorDark} />
          <div style={{ color: colors.text.darkRed }}>{error}</div>
        </div>
      )}

      {/* Message de succès du recalcul */}
      {recalculResult && (
        <div style={{
          padding: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: colors.background.lightGreen,
          border: `1px solid ${colors.status.success}`,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <CheckCircle size={20} color={colors.hover.successDark} />
          <span style={{ color: colors.text.darkGreen2, fontSize: '0.875rem' }}>
            Recalcul terminé : <strong>{recalculResult.periodes}</strong> périodes,
            <strong> {recalculResult.updated}</strong> MAJ,
            <strong> {recalculResult.created}</strong> créées
          </span>
          <button
            onClick={() => setRecalculResult(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: colors.text.darkGreen2,
              cursor: 'pointer',
              fontSize: '1.25rem',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Spinner de chargement */}
      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
          gap: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `4px solid ${colors.secondary.lightTeal}`,
            borderTop: `4px solid ${colors.primary.teal}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <AnimatedContent>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem'
          }}>
            {[1, 2, 3, 4].map(q => (
              <TrimestreCard
                key={q}
                quarter={q}
                yearFilter={yearFilter}
                data={trimestreData[q]}
              />
            ))}
          </div>
        </AnimatedContent>
      )}

      {/* Modale de confirmation de recalcul */}
      <RecalculConfirmModal
        isOpen={showRecalculConfirm}
        onClose={() => setShowRecalculConfirm(false)}
        onConfirm={confirmRecalcul}
      />
    </div>
  );
}
